/**
 * clientTheme.ts
 * ──────────────────────────────────────────────────────────────────────────────
 * Single source of truth for all client-specific and mode-specific colors.
 *
 * Structure:
 *   clientThemePalette[clientTheme][colorMode]  → palette object
 *
 * How to get the active palette in a component:
 *   import { useClientTheme } from 'utils/clientTheme';
 *   const palette = useClientTheme();   // auto-resolves client + light/dark
 */

import { useTheme } from '@mui/material';
import yokohamaPng from 'assets/logos/yokohama-logo.png';
import yokohamaSmallPng from 'assets/logos/yohokama-small-logo.png';
import { getIsYokohama } from 'utils/helpers/getIsYokohama';

// ─── Client theme ────────────────────────────────────────────────────────────

export type ClientTheme = 'default' | 'yokohama';

const isYokohamaDomain = getIsYokohama();

export const CLIENT_THEME: ClientTheme = isYokohamaDomain
  ? (process.env.REACT_APP_YOKOHAMA_CLIENT_THEME as ClientTheme) || 'yokohama'
  : (process.env.REACT_APP_CLIENT_THEME as ClientTheme) || 'default';

// ─── Palette shape ────────────────────────────────────────────────────────────

export interface ThemeModePalette {
  // Backgrounds / surfaces
  pageBackground: string;
  topBar: string;
  boxBackground: string;
  toolBarBackground: string;
  modalBackground: string;
  menuOptions: string;
  sectionBackground: string;
  paperBackground: string;

  // Grid / borders
  cartesianGrid: string;
  border: string;
  borderGrey: string;

  // Table
  tableHeader: string;

  // MUI primary color (used in createTheme primary.main)
  primary: string;
  primaryDark: string;

  // Accent / brand
  toggleSelected: string;
  toggleSelectedHover: string;

  // Text
  textPrimary: string;
  textSecondary: string;

  // Status (shared across modes — override per-theme as needed)
  success: string;
  lightSuccess: string;
  danger: string;
  warning: string;
  lightDanger: string;
  info: string;

  // Misc typography / utility
  buttonLink: string;
  white: string;
  black: string;
  cyan: string;
  lightGrey: string;
  grey: string;
  cornflowerBlue: string;
  mediumGray: string;
  lightDark: string;
  darkGrey: string;
  wheat: string;

  // Button semantic colors
  secondary: string;
  successButton: string;
  warningButton: string;
  errorButton: string;
  infoButton: string;

  // Legacy bg / svgIcons palette
  bgBlue: string;
  bgGrey: string;
  svgPrimaryFill: string;
  svgErrorFill: string;

  // Navbar / Sidebar colors
  navbarBg: string;
  navbarActiveBg: string;
  navbarActiveSubBg: string;
  navbarActiveColor: string;
  navbarHoverColor: string;

  // Breadcrumb colors
  breadcrumbBg: string;
  breadcrumbActiveColor: string;
  breadcrumbInactiveColor: string;
  topbarBg: string;
  topbarGradient: string | null;
  cardBorderColor: string;
}

// ─── Centralized palette ──────────────────────────────────────────────────────

export const clientThemePalette: Record<
  ClientTheme,
  { light: ThemeModePalette; dark: ThemeModePalette }
> = {
  // ── Default (Symboticware / symx.ai) ─────────────────────────────────────
  default: {
    light: {
      // Surfaces
      pageBackground: '#DFE5F0',
      topBar: '#FFFFFF',
      boxBackground: '#FFFFFF',
      toolBarBackground: '#F3F4F6',
      modalBackground: '#FFFFFF',
      menuOptions: '#FFFFFF',
      sectionBackground: '#D6ECFF',
      paperBackground: '#F5F5F5',
      // Grid / borders
      cartesianGrid: '#cccccc',
      border: '#D9DADA',
      borderGrey: '#BDBDBD',
      // Table
      tableHeader: '#1d3359',
      // Brand
      primary: '#3666EC',
      primaryDark: '#022859',
      toggleSelected: '#3666EC',
      toggleSelectedHover: '#2f58c9',
      // Text
      textPrimary: '#424242',
      textSecondary: '#616161',
      // Status
      success: '#28a745',
      lightSuccess: '#009161',
      danger: '#dc3545',
      warning: '#FFA403',
      lightDanger: '#EB5757',
      info: '#437F95',
      // Utility
      buttonLink: '#488AA0',
      white: '#FFFFFF',
      black: '#000000',
      cyan: '#7485A2',
      lightGrey: '#B4B4B4',
      grey: '#8A92A0',
      cornflowerBlue: '#6495ED',
      mediumGray: '#8A8A8A',
      lightDark: '#121212',
      darkGrey: '#424242',
      wheat: '#EFF4F4',
      // Button semantic
      secondary: '#AB47BC',
      successButton: '#2e7d32',
      warningButton: '#ed6c02',
      errorButton: '#d32f2f',
      infoButton: '#0288d1',
      // Legacy bg / svgIcons
      bgBlue: '#DFE5F0',
      bgGrey: '#F3F4F6',
      svgPrimaryFill: '#1049E5',
      svgErrorFill: '#A03434',
      // Navbar / Sidebar
      navbarBg: '#152642',
      navbarActiveBg: '#1a73e8',
      navbarActiveSubBg: '#1a73e877',
      navbarActiveColor: '#ffffff',
      navbarHoverColor: '#3666ec',
      // Breadcrumb
      breadcrumbBg: '#1565C0',
      breadcrumbActiveColor: '#ffffff',
      breadcrumbInactiveColor: '#ffffffcc',
      topbarBg: '#152642',
      topbarGradient: null,
      cardBorderColor: '#2F445E',
    },
    dark: {
      // Surfaces
      pageBackground: '#0A192D',
      topBar: '#152642',
      boxBackground: '#172A46',
      toolBarBackground: '#152642',
      modalBackground: '#0A2341',
      menuOptions: '#091A2F',
      sectionBackground: '#3666EC26',
      paperBackground: '#0A2341',
      // Grid / borders
      cartesianGrid: '#26364C',
      border: '#D9DADA',
      borderGrey: '#334155',
      // Table
      tableHeader: '#1C3459',
      // Brand
      primary: '#3666EC',
      primaryDark: '#021A39',
      toggleSelected: '#3666EC',
      toggleSelectedHover: '#2f58c9',
      // Text
      textPrimary: '#ffffff',
      textSecondary: '#b0bec5',
      // Status
      success: '#28a745',
      lightSuccess: '#009161',
      danger: '#dc3545',
      warning: '#FFA403',
      lightDanger: '#EB5757',
      info: '#437F95',
      // Utility
      buttonLink: '#488AA0',
      white: '#FFFFFF',
      black: '#000000',
      cyan: '#7485A2',
      lightGrey: '#B4B4B4',
      grey: '#8A92A0',
      cornflowerBlue: '#6495ED',
      mediumGray: '#8A8A8A',
      lightDark: '#121212',
      darkGrey: '#424242',
      wheat: '#EFF4F4',
      // Button semantic
      secondary: '#9c27b0',
      successButton: '#2e7d32',
      warningButton: '#ed6c02',
      errorButton: '#d32f2f',
      infoButton: '#0288d1',
      // Legacy bg / svgIcons
      bgBlue: '#DFE5F0',
      bgGrey: '#F3F4F6',
      svgPrimaryFill: '#1049E5',
      svgErrorFill: '#A03434',
      // Navbar / Sidebar
      navbarBg: '#152642',
      navbarActiveBg: '#1a73e8',
      navbarActiveSubBg: '#1a73e877',
      navbarActiveColor: '#ffffff',
      navbarHoverColor: '#3666ec',
      // Breadcrumb
      breadcrumbBg: '#1565C0',
      breadcrumbActiveColor: '#ffffff',
      breadcrumbInactiveColor: '#ffffffcc',
      topbarBg: '#152642',
      topbarGradient: null,
      cardBorderColor: '#2F445E',
    },
  },

  // ── Yokohama ─────────────────────────────────────────────────────────────
  yokohama: {
    light: {
      // Surfaces
      pageBackground: '#f6eeee',
      topBar: '#FFFFFF',
      boxBackground: '#FFFFFF',
      toolBarBackground: '#f6eeee',
      modalBackground: '#FFFFFF',
      menuOptions: '#FFFFFF',
      sectionBackground: '#FFE8EA',
      paperBackground: '#F5F5F5',
      // Grid / borders
      cartesianGrid: '#cccccc',
      border: '#D9DADA',
      borderGrey: '#BDBDBD',
      // Table
      tableHeader: '#E21E31',
      // Brand — Yokohama red
      primary: '#E21E31',
      primaryDark: '#B01626',
      toggleSelected: '#E21E31',
      toggleSelectedHover: '#B01626',
      // Text
      textPrimary: '#1A1A1A',
      textSecondary: '#616161',
      // Status
      success: '#28a745',
      lightSuccess: '#009161',
      danger: '#dc3545',
      warning: '#FFA403',
      lightDanger: '#EB5757',
      info: '#437F95',
      // Utility
      buttonLink: '#E21E31',
      white: '#FFFFFF',
      black: '#000000',
      cyan: '#7485A2',
      lightGrey: '#B4B4B4',
      grey: '#8A92A0',
      cornflowerBlue: '#E21E31', // active/selected indicator = primary brand color
      mediumGray: '#8A8A8A',
      lightDark: '#121212',
      darkGrey: '#424242',
      wheat: '#EFF4F4',
      // Button semantic
      secondary: '#AB47BC',
      successButton: '#2e7d32',
      warningButton: '#ed6c02',
      errorButton: '#d32f2f',
      infoButton: '#0288d1',
      // Legacy bg / svgIcons
      bgBlue: '#FFE8EA',
      bgGrey: '#F0F0F0',
      svgPrimaryFill: '#E21E31',
      svgErrorFill: '#A03434',
      // Navbar / Sidebar — Yokohama brand red active states
      navbarBg: '#09090b',
      navbarActiveBg: '#E21E31',
      navbarActiveSubBg: '#3b1b1d',
      navbarActiveColor: '#ffffff',
      navbarHoverColor: '#E21E31',
      // Breadcrumb — Yokohama: transparent bar, red active, grey inactive
      breadcrumbBg: '#111111',
      breadcrumbActiveColor: '#E21E31',
      breadcrumbInactiveColor: '#8A92A0',
      topbarBg: '#09090b',
      topbarGradient:
        'linear-gradient(90deg, #09090b 0%, #1a0608 40%, #2d0a0d 70%, #09090b 100%)',
      cardBorderColor: '#2a2a2a',
    },
    dark: {
      // Surfaces
      pageBackground: '#0D0D0D',
      topBar: '#1A1A1A',
      boxBackground: '#1a1a1a',
      toolBarBackground: '#1A1A1A',
      modalBackground: '#1A0A0B',
      menuOptions: '#1A0A0B',
      sectionBackground: '#E21E3126',
      paperBackground: '#1a1a1a',
      // Grid / borders
      // Grid / borders
      cartesianGrid: '#2a2a2a',
      border: '#2a2a2a',
      borderGrey: '#333333',
      // Table
      tableHeader: '#E21E31',
      // Brand — Yokohama red
      primary: '#E21E31',
      primaryDark: '#B01626',
      toggleSelected: '#E21E31',
      toggleSelectedHover: '#B01626',
      // Text
      textPrimary: '#ffffff',
      textSecondary: '#b0bec5',
      // Status
      success: '#28a745',
      lightSuccess: '#009161',
      danger: '#dc3545',
      warning: '#FFA403',
      lightDanger: '#EB5757',
      info: '#437F95',
      // Utility
      buttonLink: '#E21E31',
      white: '#FFFFFF',
      black: '#000000',
      cyan: '#7485A2',
      lightGrey: '#B4B4B4',
      grey: '#8A92A0',
      cornflowerBlue: '#E21E31', // active/selected indicator = primary brand color
      mediumGray: '#8A8A8A',
      lightDark: '#121212',
      darkGrey: '#424242',
      wheat: '#EFF4F4',
      // Button semantic
      secondary: '#9c27b0',
      successButton: '#2e7d32',
      warningButton: '#ed6c02',
      errorButton: '#d32f2f',
      infoButton: '#0288d1',
      // Legacy bg / svgIcons
      bgBlue: '#E21E3126',
      bgGrey: '#2A1214',
      svgPrimaryFill: '#E21E31',
      svgErrorFill: '#A03434',
      // Navbar / Sidebar — Yokohama brand red active states
      navbarBg: '#09090b',
      navbarActiveBg: '#E21E31',
      navbarActiveSubBg: '#3b1b1d',
      navbarActiveColor: '#ffffff',
      navbarHoverColor: '#E21E31',
      // Breadcrumb — Yokohama: transparent bar, red active, grey inactive
      breadcrumbBg: '#111111',
      breadcrumbActiveColor: '#E21E31',
      breadcrumbInactiveColor: '#8A92A0',
      topbarBg: '#09090b',
      topbarGradient:
        'linear-gradient(90deg, #09090b 0%, #1a0608 40%, #2d0a0d 70%, #09090b 100%)',
      cardBorderColor: '#2a2a2a',
    },
  },
};

// ─── Hook — resolves active palette from CLIENT_THEME + MUI color mode ───────

/**
 * Returns the palette for the current client theme + light/dark mode.
 * Must be used inside a MUI ThemeProvider.
 *
 * @example
 * const palette = useClientTheme();
 * <div style={{ background: palette.topBar }} />
 */
export const useClientTheme = (): ThemeModePalette => {
  const muiTheme = useTheme();
  const mode = muiTheme.palette.mode === 'dark' ? 'dark' : 'light';
  return clientThemePalette[CLIENT_THEME][mode];
};

// ─── Logo config ─────────────────────────────────────────────────────────────

export type LogoConfig =
  | { type: 'svg'; svgName: string }
  | { type: 'img'; src: string; alt: string };

export const getAppLogo = (): LogoConfig => {
  switch (CLIENT_THEME) {
    case 'yokohama':
      return { type: 'img', src: yokohamaPng, alt: 'Yokohama' };
    default:
      return { type: 'svg', svgName: 'sidebarLogoMain' };
  }
};

// ─── Theme identity helpers ───────────────────────────────────────────────────

/** Returns true when the active client theme is Yokohama. */
export const isYokohamaTheme = (): boolean => CLIENT_THEME === 'yokohama';

/**
 * Returns the small logo src/alt for use in UI elements like chatbot
 * avatars, loaders, and floating buttons.
 */
export const getClientLogoSmall = (): LogoConfig => {
  switch (CLIENT_THEME) {
    case 'yokohama':
      return { type: 'img', src: yokohamaSmallPng, alt: 'Yokohama' };
    default:
      return { type: 'svg', svgName: 'logo' };
  }
};
